import { hashKey, useQuery } from '@tanstack/react-query'
import { useWorkspace } from 'components/providers'
import { useMemo, useRef } from 'react'
import { useMatch, useParams } from 'react-router'
import { AggregationData, AggregationRequest, DateRange, GetAggregationProps } from 'types'
import { getAggregations, useUpdateEffect, useWorkspaces } from 'utils'
import { useEvent } from './eventUtils'
import { getOtgMemberSessionFilters } from './onTheGoUtils'
import { RoutePath } from './routing'

export interface Aggregation<T extends {} = {}> extends AggregationRequest {
  data?: AggregationData
  otherProps?: T
}

export interface UseAggregationsOptions {
  dateRange?: DateRange
}

export function useAggregations<T extends {}>(
  body: Aggregation<T>[],
  options?: UseAggregationsOptions
) {
  const onAllWorkspaces = !!useMatch(`all/*`)
  const onEvent = !!useMatch(`${RoutePath.EventInfo}/*`)
  const onOtgMember = !!useMatch(RoutePath.OnTheGoMember)
  const { id: otgMemberId } = useParams<{ id: string }>()
  const { workspaces } = useWorkspaces('emp', true)
  const { workspace } = useWorkspace()
  const { event } = useEvent()

  const otgSessionFilters = useMemo(
    () => (onOtgMember && otgMemberId ? getOtgMemberSessionFilters(otgMemberId) : []),
    [onOtgMember, otgMemberId]
  )

  const { leadCount = 0, sessionCount = 0 } = event || {}
  const totalCount = leadCount + sessionCount
  const aggregations = body.map(({ data, otherProps, ...request }) => ({
    ...request,
    filters: [...(request.filters ?? []), ...otgSessionFilters],
  }))
  const request: GetAggregationProps = {
    aggregations,
    eventIds: event?.id ? [event.id] : undefined,
    workspaceIds: onAllWorkspaces
      ? workspaces?.map(w => w.id)
      : workspace
        ? [workspace.id]
        : undefined,
    dateRange: options?.dateRange,
  }
  const enabled = Boolean(
    aggregations.length &&
    (onEvent
      ? totalCount
      : onOtgMember
        ? workspace?.id && otgMemberId
        : onAllWorkspaces
          ? workspaces?.length
          : workspace?.id)
  )

  const hasFetched = useRef(false)

  const { data, ...rest } = useQuery({
    queryKey: [hashKey([request]), options?.dateRange?.from, options?.dateRange?.to],
    queryFn: () => getAggregations(request),
    enabled,
    select: res => {
      hasFetched.current = true
      return body.map((s, index) => ({ ...s, data: res.data[index] }))
    },
  })

  useUpdateEffect(() => {
    if (hasFetched.current && !rest.isLoading) rest.refetch()
  }, [totalCount])

  return { aggregations: data || body, ...rest }
}

export function limitAggregationData(aggregation: Aggregation) {
  const { data, limit, categories, values } = aggregation
  if (!data || !limit || !categories?.length) return data

  const categoryField = categories[0]
  let categoryValues: string[] = []

  for (let row of data) {
    const categoryValue = row[categoryField] as string
    if (!categoryValues.includes(categoryValue)) categoryValues.push(categoryValue)
    if (categoryValues.length >= limit) break
  }

  const mainRows = data.filter(row => categoryValues.includes(row[categoryField] as string))
  const otherRows = data.filter(row => !categoryValues.includes(row[categoryField] as string))
  const otherRow = otherRows.reduce(
    (acc, row) => {
      for (let key in row) {
        if (values.includes(key)) acc[key] = ((acc[key] as number) || 0) + (row[key] as number)
      }
      return acc
    },
    { [categoryField]: '_limitOther' } as AggregationData[0]
  )

  return [...mainRows, otherRow]
}

